<template>
    <VPage :class="{ 'app-page--fullscreen': pageSettings.fullscreen }">
        <VPageHeader show-third-block :title="pageTitle">
            <template v-slot:second>
                <div class="app-page-header__column app-page-header__column--tools">
                    <VPageSettings v-model="pageSettings" class="prevent-close" :options="pageOptions"></VPageSettings>

                    <VTaskFilter
                        v-model="filters"
                        class="prevent-close"
                        :filter-types="[
                            TaskFilterTypes.Author,
                            TaskFilterTypes.Status,
                            TaskFilterTypes.Assignee,
                            TaskFilterTypes.Approver,
                            TaskFilterTypes.Contributor,
                            TaskFilterTypes.AuthorOrAssignee,
                            TaskFilterTypes.Deadline,
                            TaskFilterTypes.CreationDate,
                        ]"
                        @changed="fetchData"
                    ></VTaskFilter>

                    <VButtonDropdown class="prevent-close" :options="regularActions" placement="left-start">
                        <DotsSvg class="rotate-90"></DotsSvg>
                    </VButtonDropdown>

                    <VSearchField v-model="searchString" class="prevent-close" debounce-mode></VSearchField>
                </div>
            </template>
        </VPageHeader>

        <VPageContent :vertical="pageSettings.viewMode === 'table'">
            <VBoard v-if="pageSettings.viewMode === 'board'">
                <template v-for="(column, columnIndex) in columns" :key="columnIndex">
                    <VColumn
                        :column="column"
                        :editable="canUpdateColumnTitle(column)"
                        wheel-propagation
                        @set-title="setColumnTitle(column, $event)"
                    >
                        <VDraggable
                            v-model="groupedTasks[column.id]"
                            class="column__list"
                            draggable=".column__item"
                            ghost-class="task-card-ghost"
                            group="tasks"
                            item-key="id"
                            :scroll-sensitivity="200"
                            tag="ul"
                            @add="onMove(column, $event)"
                            @update="onMove(column, $event)"
                        >
                            <template v-slot:item="{ element }">
                                <li :key="element.id" class="column__item" :itemKey="element.id">
                                    <RouterLink :to="{ query: { task: element.id } }">
                                        <VTaskCard :task="element"></VTaskCard>
                                    </RouterLink>
                                </li>
                            </template>
                        </VDraggable>
                    </VColumn>
                </template>

                <div class="column column--add" v-if="canCreateColumn">
                    <div class="column__row">
                        <VButton class="button--link-underline" @click="createColumn">
                            {{ t('add-new-column') }}
                        </VButton>
                    </div>
                </div>
            </VBoard>

            <VTable
                v-model:order="order"
                v-model:table="table"
                :resetOrder="resetOrder"
                v-else-if="pageSettings.viewMode === 'table'"
            >
                <template v-for="(column, columnIndex) in columns" :key="columnIndex">
                    <VTableDataSet :title="column.title">
                        <VDraggable
                            v-model="groupedTasks[column.id]"
                            draggable=".movable-item"
                            group="tasks"
                            item-key="id"
                            :scroll-sensitivity="200"
                            tag="div"
                            @add="onMove(column, $event)"
                            @update="onMove(column, $event)"
                        >
                            <template v-slot:item="{ element, index }">
                                <RouterLink
                                    class="prevent-close"
                                    :class="{ 'movable-item': order.orderBy === 'rank' }"
                                    :to="{ query: { task: element.id } }"
                                >
                                    <VTableRow
                                        :active="element.id.toString() == route.query.task"
                                        :data="element"
                                        :index="index"
                                        :table="table"
                                        @contextmenu="openContextMenu($event, element)"
                                    ></VTableRow>
                                </RouterLink>
                            </template>
                        </VDraggable>
                    </VTableDataSet>
                </template>
            </VTable>
        </VPageContent>
    </VPage>
</template>

<script lang="ts">
// Svg
import DotsSvg from '@/assets/dots.svg';
import DownloadSvg from '@/assets/download.svg';

// Components
import VTaskCard from '../components/VTaskCard.vue';
import VPage from '../components/VPage.vue';
import VPageHeader from '../components/VPageHeader.vue';
import VPageContent from '../components/VPageContent.vue';
import VTaskFilter, { TaskFilterTypes } from '../components/VTaskFilter.vue';
import VSearchField from '../components/VSearchField.vue';
import VBoard from '../components/VBoard.vue';
import VColumn from '../components/VColumn.vue';
import VButton from '../components/VButton.vue';
import VDraggable from 'vuedraggable';
import VTable from '../components/VTable.vue';
import VTableRow from '../components/VTableRow.vue';
import VTableDataSet from '../components/VTableDataSet.vue';
import VPageSettings from '../components/VPageSettings.vue';
import VButtonDropdown from '../components/VButtonDropdown.vue';

// Other
import TaskType from '@/core/Values/TaskType';
import ProjectBoardPageMixin from './_ProjectBoardPageMixin';
import { defineComponent } from 'vue';
import { markRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTaskContextMenu } from '@/mixins/TaskApi';
import Settings from '@/core/Settings';
import useTaskTableViewer, { defaultColumns } from '@/mixins/TaskTableViewer';
import { usePageOptions } from '@/mixins/PageSettings';
import { useRoute } from 'vue-router';
import { IDropdownOption } from '@/core/Values/IDropdownOption';
import { downloadFile } from '@/utils/utils';
import TaskExportSchema from '@/core/Values/TaskExportSchema';
import store from '@/store';
import { ExportTaskRequest } from '@/core/Services/TaskService';

export default defineComponent({
    components: {
        DotsSvg,

        VTaskCard,
        VBoard,
        VColumn,
        VButton,
        VDraggable,
        VPage,
        VPageHeader,
        VPageContent,
        VTaskFilter,
        VSearchField,
        VTable,
        VTableRow,
        VTableDataSet,
        VPageSettings,
        VButtonDropdown,
    },

    mixins: [ProjectBoardPageMixin],

    setup() {
        const { t } = useI18n();
        const route = useRoute();

        const contextMenu = useTaskContextMenu();
        const table = useTaskTableViewer(Settings.UI.Table + '.shared', {
            header: true,
            columns: defaultColumns,
        });
        const defaultPageOptions = usePageOptions();
        const pageOptions = [defaultPageOptions.viewMode, defaultPageOptions.fullscreen];

        return { t, route, table, pageOptions, ...contextMenu };
    },

    data() {
        return {
            loadOnly: [TaskType.Task],

            TaskFilterTypes: markRaw(TaskFilterTypes),
        };
    },

    computed: {
        regularActions(): IDropdownOption[] {
            return [
                {
                    icon: markRaw(DownloadSvg),
                    title: this.t('export'),
                    action: () => {
                        downloadFile('/api/v1/tasks/export', {
                            ...this.tasksFilters,
                            withColumns: this.table.columns
                                .filter((column) => column.visible)
                                .map((column) => column.key),
                            schema: TaskExportSchema.List,
                            language: store.state.userSettings.locale,
                        } as ExportTaskRequest);
                    },
                },
            ];
        },
    },

    created() {
        this.searchString = '';
    },
});
</script>
